import { useState, useEffect } from "react";
import {
  getOwnerSubscriptionCurrent,
  getOwnerSubscriptionPlans,
  getOwnerSubscriptionHistory,
} from "../../../services/propertyOwner/propertyOwnerServices";

export default function MySubscription() {
  const [currentPlan, setCurrentPlan] = useState(null);
  const [currentLoading, setCurrentLoading] = useState(true);

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [billingYearly, setBillingYearly] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize] = useState(20);

  useEffect(() => {
    fetchCurrent();
    fetchPlans();
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [historyPage]);

  const fetchCurrent = async () => {
    setCurrentLoading(true);
    try {
      const res = await getOwnerSubscriptionCurrent();
      if (res.statusCode === 1) setCurrentPlan(res.data);
    } catch {
      console.log("Failed to load current plan");
    } finally {
      setCurrentLoading(false);
    }
  };

  const fetchPlans = async () => {
    setPlansLoading(true);
    try {
      const res = await getOwnerSubscriptionPlans();
      if (res.statusCode === 1) setPlans(res.data || []);
    } catch {
      console.log("Failed to load plans");
    } finally {
      setPlansLoading(false);
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await getOwnerSubscriptionHistory({
        page: historyPage,
        pageSize: historyPageSize,
      });
      if (res.statusCode === 1) {
        setHistory(res.data.items || []);
        setHistoryTotal(res.data.totalCount);
        setHistoryTotalPages(res.data.totalPages);
      }
    } catch {
      console.log("Failed to load history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const formatDate = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "—";

  const shimmerStyle = {
    height: 14,
    borderRadius: 6,
    background:
      "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.4s infinite",
  };

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title" id="page-title">
              My Subscription
            </div>
            <div className="page-sub" id="page-sub">
              View and manage your plan
            </div>
          </div>
          <div className="topbar-right" id="topbar-right" />
        </div>

        <div className="content" id="content-area">
          {/* Hero */}
          <div className="sub-hero">
            <h2>Subscription Plans</h2>
            <p>
              Choose a plan that fits your property portfolio. All plans include
              core tenant management, billing, and KYC features.
            </p>
            <div className="sub-notice">
              ⚠{" "}
              <strong>
                Before purchasing any package, you must integrate the required
                APIs.
              </strong>{" "}
              No refund will be processed in any case.
              <br />
              If you have an active package and purchase another, all remaining
              limits and validity will be flushed out and the new package will
              be applicable from today.
            </div>
          </div>

          {/* Current Plan */}
          <div
            className="card mb16"
            style={{ borderLeft: "4px solid var(--teal)" }}
          >
            {currentLoading ? (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                <div style={{ ...shimmerStyle, width: "30%" }} />
                <div style={{ ...shimmerStyle, width: "50%" }} />
              </div>
            ) : currentPlan ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 12,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "var(--teal)",
                    }}
                  >
                    Your Current Plan: {currentPlan.packageName}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--text-secondary)",
                      marginTop: 4,
                    }}
                  >
                    Valid till:{" "}
                    <strong>{formatDate(currentPlan.endDate)}</strong> ·{" "}
                    {currentPlan.daysRemaining} days remaining
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {[
                    {
                      label: "Properties",
                      value: currentPlan.maxProperties ?? "∞",
                    },
                    { label: "Tenants", value: currentPlan.maxTenants ?? "∞" },
                    {
                      label: "Collections",
                      value: currentPlan.maxCollections ?? "∞",
                    },
                    { label: "Rooms", value: currentPlan.maxRooms ?? "∞" },
                    { label: "SMS", value: currentPlan.smsLimit ?? "∞" },
                    { label: "WhatsApp", value: currentPlan.waLimit ?? "∞" },
                    { label: "Storage", value: `${currentPlan.storageGB} GB` },
                  ].map((item) => (
                    <div
                      key={item.label}
                      style={{
                        textAlign: "center",
                        padding: "10px 18px",
                        background: "var(--bg-secondary)",
                        borderRadius: "var(--radius-md)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 18,
                          fontWeight: 700,
                          color: "var(--teal)",
                        }}
                      >
                        {item.value}
                      </div>
                      <div
                        style={{ fontSize: 10, color: "var(--text-secondary)" }}
                      >
                        {item.label}
                      </div>
                    </div>
                  ))}
                </div>
                <button className="btn btn-teal">Renew Plan</button>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                No active subscription found.
              </div>
            )}
          </div>

          {/* Billing Toggle */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              marginBottom: 20,
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 500 }}>Monthly</span>
            <label className="toggle">
              <input
                type="checkbox"
                checked={billingYearly}
                onChange={(e) => setBillingYearly(e.target.checked)}
              />
              <span className="toggle-slider" />
            </label>
            <span style={{ fontSize: 13, fontWeight: 500 }}>
              Yearly{" "}
              <span
                style={{
                  background: "var(--green-light)",
                  color: "var(--green)",
                  fontSize: 10,
                  padding: "2px 8px",
                  borderRadius: 99,
                  fontWeight: 600,
                  marginLeft: 4,
                }}
              >
                Save up to 17%
              </span>
            </span>
          </div>

          {/* Package Cards */}
          {plansLoading ? (
            <div className="pkg-grid">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    background: "var(--bg-secondary)",
                    border: "0.5px solid var(--border-light)",
                    borderRadius: "var(--radius-md)",
                    padding: 24,
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  <div style={{ ...shimmerStyle, width: "50%" }} />
                  <div style={{ ...shimmerStyle, width: "70%" }} />
                  <div style={{ ...shimmerStyle, width: "40%", height: 28 }} />
                  {Array.from({ length: 5 }).map((_, j) => (
                    <div key={j} style={{ ...shimmerStyle, width: "80%" }} />
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div className="pkg-grid">
              {plans.map((plan) => (
                <div
                  key={plan.packageId}
                  className={`pkg-card ${plan.isFeatured ? "featured" : ""}`}
                  style={{
                    borderColor: plan.isCurrentPlan
                      ? "var(--teal)"
                      : "var(--border-light)",
                    position: "relative",
                  }}
                >
                  {plan.isFeatured && !plan.isCurrentPlan && (
                    <div className="pkg-popular">Most Popular</div>
                  )}
                  <div className="pkg-name">{plan.name}</div>
                  <div className="pkg-tagline">{plan.tagline}</div>
                  <div className="pkg-price">
                    ₹{" "}
                    {(billingYearly
                      ? plan.priceYearly
                      : plan.priceMonthly
                    )?.toLocaleString("en-IN")}
                    <span>/ {billingYearly ? "Year" : "Month"}</span>
                  </div>
                  {!billingYearly && plan.priceYearly && (
                    <div className="pkg-validity">
                      ₹ {plan.priceYearly?.toLocaleString("en-IN")} / Year
                    </div>
                  )}
                  <hr className="pkg-divider" />
                  <ul className="pkg-features">
                    <li>
                      Up to <strong>{plan.maxProperties ?? "Unlimited"}</strong>{" "}
                      Properties
                    </li>
                    <li>
                      Up to <strong>{plan.maxTenants ?? "Unlimited"}</strong>{" "}
                      Tenants
                    </li>
                    <li>
                      Up to{" "}
                      <strong>{plan.maxCollections ?? "Unlimited"}</strong>{" "}
                      Collections
                    </li>
                    <li>
                      Up to <strong>{plan.maxRooms ?? "Unlimited"}</strong>{" "}
                      Rooms
                    </li>
                    <li>{plan.smsLimit ?? "Unlimited"} SMS / month</li>
                    <li>{plan.waLimit ?? "Unlimited"} WhatsApp messages</li>
                    <li>{plan.storageGB} GB Document Storage</li>
                    {plan.features?.map((f, i) => (
                      <li key={i}>{f}</li>
                    ))}
                  </ul>
                  {plan.isCurrentPlan ? (
                    <button className="pkg-btn pkg-current" disabled>
                      ✓ Current Plan
                    </button>
                  ) : (
                    <button className="pkg-btn pkg-btn-outline">
                      {currentPlan &&
                      plan.priceMonthly <
                        (plans.find((p) => p.isCurrentPlan)?.priceMonthly ?? 0)
                        ? `Downgrade to ${plan.name}`
                        : `Upgrade to ${plan.name}`}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Subscription History */}
          <div className="card" style={{ marginTop: 24 }}>
            <div className="card-title">Subscription History</div>
            <div style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>S.N.</th>
                    <th>Plan</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Start Date</th>
                    <th>Expiry</th>
                    <th>Payment Mode</th>
                    <th>Status</th>
                    <th>Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {historyLoading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 9 }).map((_, j) => (
                          <td key={j}>
                            <div style={{ ...shimmerStyle, width: "70%" }} />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : history.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        style={{
                          textAlign: "center",
                          color: "var(--text-tertiary)",
                          padding: 24,
                        }}
                      >
                        No subscription history found.
                      </td>
                    </tr>
                  ) : (
                    history.map((h, idx) => (
                      <tr key={h.subscriptionId}>
                        <td className="sn-col">
                          {(historyPage - 1) * historyPageSize + idx + 1}
                        </td>
                        <td>{h.planName}</td>
                        <td style={{ fontSize: 11 }}>{h.planType}</td>
                        <td>₹{h.amount?.toLocaleString("en-IN")}</td>
                        <td style={{ fontSize: 11, whiteSpace: "nowrap" }}>
                          {formatDate(h.startDate)}
                        </td>
                        <td style={{ fontSize: 11, whiteSpace: "nowrap" }}>
                          {formatDate(h.endDate)}
                        </td>
                        <td style={{ fontSize: 11 }}>{h.paymentMode}</td>
                        <td>
                          <span
                            className={`badge ${h.status === "Active" ? "badge-active" : "badge-gray"}`}
                          >
                            {h.status}
                          </span>
                        </td>
                        <td>
                          {h.invoiceUrl && h.invoiceUrl !== "#" ? (
                            <a
                              href={h.invoiceUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-xs"
                            >
                              Download
                            </a>
                          ) : (
                            <button className="btn btn-xs" disabled>
                              N/A
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div
              style={{
                marginTop: 12,
                display: "flex",
                justifyContent: "space-between",
                fontSize: 12,
                color: "var(--text-secondary)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span>
                  Showing <strong>{history.length}</strong> of{" "}
                  <strong>{historyTotal}</strong>
                </span>
              </div>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <button
                  className="btn btn-xs"
                  disabled={historyPage === 1}
                  onClick={() => setHistoryPage(historyPage - 1)}
                >
                  ‹ Prev
                </button>
                <span
                  style={{
                    padding: "2px 8px",
                    background: "var(--blue)",
                    color: "#fff",
                    borderRadius: 5,
                    fontSize: 11,
                  }}
                >
                  {historyPage}
                </span>
                <button
                  className="btn btn-xs"
                  disabled={historyPage === historyTotalPages}
                  onClick={() => setHistoryPage(historyPage + 1)}
                >
                  Next ›
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
